import React, { useMemo, useCallback, memo, useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { Virtuoso } from "react-virtuoso";
import { EmptyState } from "@toddle-edu/ds-web";
import { NoSearchResultsIllustration } from "@toddle-edu/ds-theme";
import { useI18n } from "Hooks";
import useSchoolMembersCounts from "AppComponents/SharedEntities/CustomEntities/hooks/useSchoolMemberHierarchy";
import SchoolHeader from "AppComponents/SharedEntities/common/SchoolHeader";
import { useGradesContext } from "../../context/GradesContext";
import useServerFilteredGrades from "../../hooks/useServerFilteredGrades";
import {
  computeGradeCountSubtitle,
  getFilteredGradeHierarchy,
} from "../../modules/GradesUtils";
import GradeRow from "../GradeRow";
import classes from "./GradesList.scss";

// ─── Checkbox states ──────────────────────────────────────────────────────────

const CHECKBOX_STATE = {
  INDETERMINATE: "INDETERMINATE",
  DETERMINATE: "DETERMINATE",
  EMPTY: "EMPTY",
};

// ─── SchoolHeaderWithCounts ───────────────────────────────────────────────────

const SchoolHeaderWithCounts = memo(
  ({
    schoolId,
    gradeIds,
    selectedGradeIdSet,
    setSelectedGradeList,
    gradeDetailsMap,
    viewOnly,
    counts,
    countsLoading,
    selectedUserTypes,
    onCollapseToggle,
    isCollapsed,
  }) => {
    const { t } = useI18n(["common"]);

    const gradeCount = _.size(gradeIds);

    const subtitle = useMemo(() => {
      if (viewOnly) return `${gradeCount} ${t("common:grade_plural")}`;
      if (countsLoading && !counts) return null;
      const participantSubtitle = computeGradeCountSubtitle(
        counts,
        selectedUserTypes,
        t
      );
      return participantSubtitle
        ? `${gradeCount} ${t("common:grade_plural")} | ${participantSubtitle}`
        : `${gradeCount} ${t("common:grade_plural")}`;
    }, [viewOnly, countsLoading, counts, gradeCount, selectedUserTypes, t]);

    const checkboxState = useMemo(() => {
      let checkedCount = 0;
      _.forEach(gradeIds, id => {
        if (selectedGradeIdSet.has(id)) checkedCount++;
      });
      if (checkedCount === 0) return CHECKBOX_STATE.EMPTY;
      if (checkedCount === _.size(gradeIds)) return CHECKBOX_STATE.DETERMINATE;
      return CHECKBOX_STATE.INDETERMINATE;
    }, [gradeIds, selectedGradeIdSet]);

    const handleCheck = useCallback(() => {
      if (checkboxState === CHECKBOX_STATE.DETERMINATE) {
        const gradeIdSet = new Set(gradeIds);
        setSelectedGradeList(prev =>
          _.filter(prev, item => !gradeIdSet.has(item.id))
        );
      } else {
        setSelectedGradeList(prev => {
          const existingIds = new Set(_.map(prev, "id"));
          const toAdd = _.reduce(
            gradeIds,
            (acc, id) => {
              if (!existingIds.has(id)) {
                const details = _.get(gradeDetailsMap, id, {});
                acc.push({ id, name: details.name || "" });
              }
              return acc;
            },
            []
          );
          return [...prev, ...toAdd];
        });
      }
    }, [checkboxState, gradeIds, setSelectedGradeList, gradeDetailsMap]);

    return (
      <SchoolHeader
        schoolId={schoolId}
        subtitle={subtitle}
        isSubtitleLoading={!viewOnly && countsLoading && !counts}
        isCollapsed={isCollapsed}
        onCollapseToggle={onCollapseToggle}
        viewOnly={viewOnly}
        isChecked={checkboxState === CHECKBOX_STATE.DETERMINATE}
        isIndeterminate={checkboxState === CHECKBOX_STATE.INDETERMINATE}
        onCheck={handleCheck}
      />
    );
  }
);

SchoolHeaderWithCounts.displayName = "SchoolHeaderWithCounts";

// ─── GradesList ───────────────────────────────────────────────────────────────

const GradesList = ({ testId }) => {
  const { t } = useI18n(["common"]);
  const {
    hierarchy,
    hierarchyLoading,
    gradeDetailsMap,
    allGradeIds,
    schools,
    selectedGradeList,
    setSelectedGradeList,
    viewOnly,
    organizationId,
    selectedUserTypes,
    filteredGradeIds,
    searchText,
    selectedSchoolIds,
  } = useGradesContext();

  const {
    schoolMembersCountMap,
    loading: countsLoading,
  } = useSchoolMembersCounts(organizationId);

  const {
    grades: serverGrades,
    loading: serverGradesLoading,
  } = useServerFilteredGrades({ organizationId, searchText });

  const schoolsMap = useMemo(() => _.keyBy(schools, "id"), [schools]);

  const showSchoolHeaders = _.size(schools) > 1;

  const isSearching = Boolean(searchText);

  // Filter hierarchy by selected schools (matches CoursesList pattern)
  const filteredHierarchy = useMemo(
    () => getFilteredGradeHierarchy(hierarchy, selectedSchoolIds),
    [hierarchy, selectedSchoolIds]
  );

  // Per-school grade IDs for checkbox state computation
  const schoolGradeIdsMap = useMemo(() => {
    const map = {};
    _.forEach(filteredHierarchy, (gradeIds, schoolId) => {
      map[schoolId] = gradeIds;
    });
    return map;
  }, [filteredHierarchy]);

  const filteredGradeIdSet = useMemo(() => new Set(filteredGradeIds), [
    filteredGradeIds,
  ]);

  const [collapsedSchools, setCollapsedSchools] = useState(new Set());

  const flatItems = useMemo(() => {
    if (hierarchyLoading && _.isEmpty(allGradeIds)) {
      return _.times(6, i => ({
        type: "grade",
        key: `skeleton-${i}`,
        gradeId: `skeleton-${i}`,
        schoolId: null,
      }));
    }
    if (isSearching && serverGradesLoading) {
      return _.times(6, i => ({
        type: "grade",
        key: `skeleton-${i}`,
        gradeId: `skeleton-${i}`,
        schoolId: null,
      }));
    }

    if (isSearching) {
      // Group server-returned grades by school (curriculumProgram.id === schoolId)
      const gradesBySchool = _.groupBy(serverGrades, g =>
        _.get(g, "curriculumProgram.id")
      );
      const items = [];
      _.forEach(gradesBySchool, (grades, schoolId) => {
        if (_.isEmpty(grades)) return;
        if (showSchoolHeaders) {
          const school = _.get(schoolsMap, schoolId, {});
          if (_.isEmpty(school)) return;
          const schoolGradeIds = _.map(grades, "id");
          items.push({
            type: "school",
            key: `school-${schoolId}`,
            schoolId,
            schoolName: _.get(school, "label", schoolId),
            schoolLogo: _.get(school, "logo"),
            schoolGradeIds,
          });
        }
        _.forEach(grades, grade => {
          items.push({
            type: "grade",
            key: `search-${grade.id}`,
            gradeId: grade.id,
            schoolId,
          });
        });
      });
      return items;
    }

    const items = [];
    _.forEach(filteredHierarchy, (gradeIds, schoolId) => {
      if (showSchoolHeaders) {
        const school = _.get(schoolsMap, schoolId, {});
        if (_.isEmpty(school)) return; // Skip if school details are missing
        const schoolName = _.get(school, "label", schoolId);
        const schoolLogo = _.get(school, "logo");
        const schoolGradeIds = _.get(schoolGradeIdsMap, schoolId, []);
        items.push({
          type: "school",
          key: `school-${schoolId}`,
          schoolId,
          schoolName,
          schoolLogo,
          schoolGradeIds,
        });
      }
      if (collapsedSchools.has(schoolId)) return;
      _.forEach(gradeIds, gradeId => {
        if (!filteredGradeIdSet.has(gradeId)) return;
        items.push({
          type: "grade",
          key: `${schoolId}-${gradeId}`,
          gradeId,
          schoolId,
        });
      });
    });
    return items;
  }, [
    hierarchyLoading,
    allGradeIds,
    isSearching,
    serverGradesLoading,
    serverGrades,
    filteredHierarchy,
    showSchoolHeaders,
    schoolsMap,
    schoolGradeIdsMap,
    filteredGradeIdSet,
    collapsedSchools,
  ]);

  const selectedGradeIdSet = useMemo(
    () => new Set(_.map(selectedGradeList, "id")),
    [selectedGradeList]
  );

  const itemContent = useCallback(
    (_index, item) => {
      if (item.type === "school") {
        const handlerCollapsedToggle = () => {
          setCollapsedSchools(prev => {
            const newSet = new Set(prev);
            if (newSet.has(item.schoolId)) {
              newSet.delete(item.schoolId);
            } else {
              newSet.add(item.schoolId);
            }
            return newSet;
          });
        };
        return (
          <SchoolHeaderWithCounts
            schoolId={item.schoolId}
            gradeIds={item.schoolGradeIds}
            selectedGradeIdSet={selectedGradeIdSet}
            setSelectedGradeList={setSelectedGradeList}
            gradeDetailsMap={gradeDetailsMap}
            viewOnly={viewOnly}
            counts={schoolMembersCountMap[item.schoolId]}
            countsLoading={countsLoading}
            selectedUserTypes={selectedUserTypes}
            onCollapseToggle={handlerCollapsedToggle}
            isCollapsed={collapsedSchools.has(item.schoolId)}
          />
        );
      }
      return (
        <div className={classes.gradeRow} data-id={item.gradeId}>
          <GradeRow gradeId={item.gradeId} testId={testId} />
        </div>
      );
    },
    [
      selectedGradeIdSet,
      setSelectedGradeList,
      gradeDetailsMap,
      viewOnly,
      testId,
      schoolMembersCountMap,
      countsLoading,
      selectedUserTypes,
      collapsedSchools,
    ]
  );

  if (!hierarchyLoading && _.isEmpty(allGradeIds)) {
    return (
      <EmptyState
        dsVersion="2.0"
        illustration={NoSearchResultsIllustration}
        title={t("common:no_grades_found")}
      />
    );
  }

  if (
    !hierarchyLoading &&
    searchText &&
    !serverGradesLoading &&
    _.isEmpty(serverGrades)
  ) {
    return (
      <EmptyState
        dsVersion="2.0"
        illustration={NoSearchResultsIllustration}
        title={t("common:noResultHeading")}
      />
    );
  }

  return (
    <Virtuoso
      data={flatItems}
      computeItemKey={(_index, item) => item.key}
      itemContent={itemContent}
      increaseViewportBy={400}
      className={classes.virtuosoContainer}
    />
  );
};

GradesList.propTypes = {
  testId: PropTypes.string,
};

export default GradesList;
